import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { ChevronsUpDown, Check, DollarSign } from 'lucide-react'
import { searchSchools } from '@/lib/api'
import PreferenceMultiSelect from '@/components/PreferenceMultiSelect'
import SportsMultiSelect from '@/components/SportsMultiSelect'
import { toast } from '@/components/ui/sonner'
import { interestSuggestions, contentStyleSuggestions, brandCategorySuggestions } from '@/config'

type PreferenceField = 'interests' | 'contentStyles' | 'preferredBrands'
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface AdditionalInfoSettingsProps {
  athlete: {
    id?: string | number
    name?: string
    email?: string
    school_id?: number | null
    school?: { id: number | string; name: string } | null
    sports?: Array<{ id: number; name: string }> | number[]
    preferences?: {
      interests: string[]
      contentStyles: string[]
      preferredBrands: string[]
      excludedCategories: string[]
    }
  }
  updatePreferences: (field: string, values: string[]) => Promise<void>
  updateProfile?: (updates: Record<string, unknown>) => Promise<void>
  updateSports?: (sportIds: number[]) => Promise<void>
  updateSchool?: (schoolId: number | null) => Promise<void>
}

const AdditionalInfoSettings = ({
  athlete,
  updatePreferences,
  updateProfile,
  updateSports,
  updateSchool,
}: AdditionalInfoSettingsProps) => {
  const [selectedSportIds, setSelectedSportIds] = useState<number[]>([])
  const [schoolOpen, setSchoolOpen] = useState(false)
  const [schoolSearch, setSchoolSearch] = useState('')
  const [schoolResults, setSchoolResults] = useState<{ id: number; name: string }[]>([])
  const [selectedSchool, setSelectedSchool] = useState<{ id: number; name: string } | null>(null)
  const [schoolSaving, setSchoolSaving] = useState(false)
  const schoolSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // const [preferredRate, setPreferredRate] = useState('') // Removed in favor of detailed rates
  const [rates, setRates] = useState({
    instagramPosts: '',
    instagramStories: '',
    instagramReels: '',
    tiktokVideos: '',
    youtubeVideos: '',
    youtubeShorts: '',
  })
  const [localInterests, setLocalInterests] = useState<string[]>([])
  const [localContentStyles, setLocalContentStyles] = useState<string[]>([])
  const [localPreferredBrands, setLocalPreferredBrands] = useState<string[]>([])
  const [saveStatuses, setSaveStatuses] = useState<Record<PreferenceField, SaveStatus>>({
    interests: 'idle',
    contentStyles: 'idle',
    preferredBrands: 'idle',
  })
  const saveStatusTimers = useRef<Partial<Record<PreferenceField, ReturnType<typeof setTimeout>>>>(
    {},
  )
  const [isSaving, setIsSaving] = useState(false)
  const preferenceInputHelp = 'Type a value and press Enter, or click a suggestion to add.'

  // Initialize local state from athlete data
  useEffect(() => {
    if (athlete?.school) {
      setSelectedSchool({ id: Number(athlete.school.id), name: athlete.school.name })
    } else {
      setSelectedSchool(null)
    }
  }, [athlete?.school])

  useEffect(() => {
    if (athlete?.preferences) {
      setLocalInterests(athlete.preferences.interests || [])
      setLocalContentStyles(athlete.preferences.contentStyles || [])
      setLocalPreferredBrands(athlete.preferences.preferredBrands || [])
    }

    // Initialize sports - handle both array of objects and array of numbers
    if (athlete?.sports) {
      if (Array.isArray(athlete.sports)) {
        if (athlete.sports.length > 0) {
          // Check if it's an array of objects with id property
          if (
            typeof athlete.sports[0] === 'object' &&
            athlete.sports[0] !== null &&
            'id' in athlete.sports[0]
          ) {
            setSelectedSportIds(athlete.sports.map((s: any) => s.id))
          } else {
            // It's already an array of IDs
            setSelectedSportIds(athlete.sports as number[])
          }
        }
      }
    }
  }, [athlete?.preferences, athlete?.sports])

  useEffect(() => {
    const timers = saveStatusTimers.current
    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer)
      })
    }
  }, [])

  const handleSchoolSearch = (value: string) => {
    setSchoolSearch(value)
    if (schoolSearchTimer.current) clearTimeout(schoolSearchTimer.current)
    if (value.trim().length < 2) {
      setSchoolResults([])
      return
    }
    schoolSearchTimer.current = setTimeout(async () => {
      try {
        const results = await searchSchools(value.trim())
        setSchoolResults(results)
      } catch {
        setSchoolResults([])
      }
    }, 300)
  }

  const handleSchoolSelect = async (school: { id: number; name: string }) => {
    setSchoolOpen(false)
    setSchoolSearch('')
    setSchoolResults([])
    if (selectedSchool?.id === school.id) return
    setSelectedSchool(school)
    if (!updateSchool) return
    setSchoolSaving(true)
    try {
      await updateSchool(school.id)
      toast('School updated', { description: `Your school has been set to ${school.name}.` })
    } catch {
      setSelectedSchool(
        athlete?.school ? { id: Number(athlete.school.id), name: athlete.school.name } : null,
      )
      toast.error('Error', { description: 'Failed to update school. Please try again.' })
    } finally {
      setSchoolSaving(false)
    }
  }

  const handleSaveAdditionalInfo = async () => {
    setIsSaving(true)
    try {
      // Build array of promises for all updates
      const updates = [
        updatePreferences('interests', localInterests),
        updatePreferences('contentStyles', localContentStyles),
        updatePreferences('preferredBrands', localPreferredBrands),
      ]

      // Update sports if the function is provided
      if (updateSports && selectedSportIds.length > 0) {
        updates.push(updateSports(selectedSportIds))
      }

      // Save all updates at once
      await Promise.all(updates)

      toast('Preferences updated', {
        description: 'Your additional information has been updated.',
      })
    } catch (error) {
      toast.error('Error', { description: 'Failed to update preferences. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  const setFieldSaveStatus = (field: PreferenceField, status: SaveStatus) => {
    setSaveStatuses((prev) => ({ ...prev, [field]: status }))
  }

  const setFieldSavedThenReset = (field: PreferenceField) => {
    if (saveStatusTimers.current[field]) {
      clearTimeout(saveStatusTimers.current[field])
    }

    setFieldSaveStatus(field, 'saved')
    saveStatusTimers.current[field] = setTimeout(() => {
      setFieldSaveStatus(field, 'idle')
      saveStatusTimers.current[field] = undefined
    }, 1600)
  }

  const savePreferenceField = async (field: PreferenceField, values: string[]) => {
    setFieldSaveStatus(field, 'saving')
    try {
      await updatePreferences(field, values)
      setFieldSavedThenReset(field)
    } catch (error) {
      setFieldSaveStatus(field, 'error')
      toast.error('Could not save preference', {
        description: 'Your latest change was not saved. Please try again.',
      })
    }
  }

  const handleInterestsChange = (values: string[]) => {
    setLocalInterests(values)
    void savePreferenceField('interests', values)
  }

  const handleContentStylesChange = (values: string[]) => {
    setLocalContentStyles(values)
    void savePreferenceField('contentStyles', values)
  }

  const handlePreferredBrandsChange = (values: string[]) => {
    setLocalPreferredBrands(values)
    void savePreferenceField('preferredBrands', values)
  }

  const handleSportsChange = async (sportIds: number[]) => {
    setSelectedSportIds(sportIds)
    if (updateSports) {
      try {
        await updateSports(sportIds)
      } catch (error) {
        toast.error('Error saving sports', {
          description: 'Failed to update your sports selection.',
        })
      }
    }
  }

  return (
    <Card variant="dark" className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-foreground">Additional Information</CardTitle>
        <CardDescription className="text-gray-600">
          Share the values, causes, and brand categories that align with you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* School Name */}
        <div className="space-y-2">
          <Label className="text-[#9EA3AE]">School Name</Label>
          <Popover open={schoolOpen} onOpenChange={setSchoolOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={schoolOpen}
                disabled={schoolSaving}
                className="w-full h-12 justify-between rounded-lg bg-card text-foreground border-foreground/10 hover:bg-foreground/10 hover:text-foreground font-normal"
              >
                <span className={selectedSchool ? 'text-foreground' : 'text-gray-600'}>
                  {schoolSaving
                    ? 'Saving...'
                    : selectedSchool
                      ? selectedSchool.name
                      : 'Search for your school...'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-600" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-full p-0 bg-card border-foreground/10"
              style={{ width: 'var(--radix-popover-trigger-width)' }}
            >
              <Command className="bg-transparent">
                <CommandInput
                  placeholder="Search school..."
                  value={schoolSearch}
                  onValueChange={handleSchoolSearch}
                  className="text-foreground placeholder:text-gray-600 border-b border-foreground/10"
                />
                <CommandList>
                  {schoolSearch.trim().length < 2 ? (
                    <CommandEmpty className="text-gray-600 py-3 text-sm text-center">
                      Type at least 2 characters to search.
                    </CommandEmpty>
                  ) : schoolResults.length === 0 ? (
                    <CommandEmpty className="text-gray-600 py-3 text-sm text-center">
                      No schools found.
                    </CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {schoolResults.map((school) => (
                        <CommandItem
                          key={school.id}
                          value={school.name}
                          onSelect={() => handleSchoolSelect(school)}
                          className="text-foreground hover:bg-foreground/10 cursor-pointer"
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${selectedSchool?.id === school.id ? 'opacity-100' : 'opacity-0'}`}
                          />
                          {school.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Divider */}
        <div className="border-t border-foreground/10"></div>

        {/* Sports */}
        <div className="space-y-2">
          <SportsMultiSelect
            label="Sports"
            description="Select all sports you participate in"
            selectedSportIds={selectedSportIds}
            onSportsChange={handleSportsChange}
            placeholder="Add a sport..."
          />
        </div>
        {/* Saving indicator for sports could be added here if desired, 
            but for now we just auto-save silently/immediately as requested along with the visual feedback from the component */}

        {/* Divider */}
        <div className="border-t border-foreground/10"></div>
        <div className="space-y-3">
          <Label className="text-[#9EA3AE]">Preferred Rates</Label>

          <div className="text-sm text-gray-600">
            Set your preferred rates based on our{' '}
            <Dialog>
              <DialogTrigger asChild>
                <button className="text-[#4d7c0f] hover:underline font-medium focus:outline-hidden">
                  Rate Guidelines
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg bg-card border border-border text-foreground">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-2xl font-normal">
                    <div className="w-10 h-10 rounded-full border-2 border-[#5DBED9] flex items-center justify-center shrink-0">
                      <svg
                        className="w-5 h-5 text-[#5DBED9]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                    </div>
                    Rate Guidelines
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4 pb-2 border-b border-foreground/10">
                    <div className="text-[#9EA3AE] font-bold text-xs uppercase">
                      # of Followers
                    </div>
                    <div className="text-[#9EA3AE] font-bold text-xs uppercase">Rate Range</div>
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div className="text-foreground font-medium text-sm">Under 10K</div>
                      <div className="text-foreground text-sm">$50 - $150/post</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div className="text-foreground font-medium text-sm">
                        10k to 50k followers
                      </div>
                      <div className="text-foreground text-sm">$150 - $500/post</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div className="text-foreground font-medium text-sm">
                        50K to 100k followers
                      </div>
                      <div className="text-foreground text-sm">$500 - $1,000/post</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div className="text-foreground font-medium text-sm">
                        100k to 500k followers
                      </div>
                      <div className="text-foreground text-sm">$1,000 - $5,000/post</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div className="text-foreground font-medium text-sm">500k+ followers</div>
                      <div className="text-foreground text-sm">$5,000+ /post</div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>{' '}
            as a starting point.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="instagram-posts" className="text-foreground">
                Instagram Posts
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <Input
                  id="instagram-posts"
                  type="text"
                  placeholder="0"
                  value={rates.instagramPosts}
                  onChange={(e) =>
                    setRates({ ...rates, instagramPosts: e.target.value.replace(/[^0-9]/g, '') })
                  }
                  className="pl-9 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagram-stories" className="text-foreground">
                Instagram Stories
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <Input
                  id="instagram-stories"
                  type="text"
                  placeholder="0"
                  value={rates.instagramStories}
                  onChange={(e) =>
                    setRates({
                      ...rates,
                      instagramStories: e.target.value.replace(/[^0-9]/g, ''),
                    })
                  }
                  className="pl-9 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagram-reels" className="text-foreground">
                Instagram Reels
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <Input
                  id="instagram-reels"
                  type="text"
                  placeholder="0"
                  value={rates.instagramReels}
                  onChange={(e) =>
                    setRates({ ...rates, instagramReels: e.target.value.replace(/[^0-9]/g, '') })
                  }
                  className="pl-9 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tiktok-videos" className="text-foreground">
                TikTok Videos
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <Input
                  id="tiktok-videos"
                  type="text"
                  placeholder="0"
                  value={rates.tiktokVideos}
                  onChange={(e) =>
                    setRates({ ...rates, tiktokVideos: e.target.value.replace(/[^0-9]/g, '') })
                  }
                  className="pl-9 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube-videos" className="text-foreground">
                YouTube Videos
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <Input
                  id="youtube-videos"
                  type="text"
                  placeholder="0"
                  value={rates.youtubeVideos}
                  onChange={(e) =>
                    setRates({ ...rates, youtubeVideos: e.target.value.replace(/[^0-9]/g, '') })
                  }
                  className="pl-9 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube-shorts" className="text-foreground">
                YouTube Shorts
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <Input
                  id="youtube-shorts"
                  type="text"
                  placeholder="0"
                  value={rates.youtubeShorts}
                  onChange={(e) =>
                    setRates({ ...rates, youtubeShorts: e.target.value.replace(/[^0-9]/g, '') })
                  }
                  className="pl-9 bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-foreground/10"></div>

        {/* Interests Section */}
        <div className="space-y-6">
          <h3 className="text-[#9EA3AE] font-bold text-base uppercase">Interests</h3>

          <div className="space-y-2">
            <p className="text-sm text-foreground">What topics are you passionate about?</p>
            <PreferenceMultiSelect
              label=""
              description=""
              selectedItems={localInterests}
              onItemsChange={handleInterestsChange}
              placeholder="Add topic..."
              suggestions={interestSuggestions}
              helperText={preferenceInputHelp}
              saveStatus={saveStatuses.interests}
              showHelperOnFocusOnly
              reserveHelperSpace
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm text-foreground">What types of content do you enjoy creating?</p>
            <PreferenceMultiSelect
              label=""
              description=""
              selectedItems={localContentStyles}
              onItemsChange={handleContentStylesChange}
              placeholder="Add content style..."
              suggestions={contentStyleSuggestions}
              helperText={preferenceInputHelp}
              saveStatus={saveStatuses.contentStyles}
              showHelperOnFocusOnly
              reserveHelperSpace
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm text-foreground">
              What types of brands would you like to work with?
            </p>
            <PreferenceMultiSelect
              label=""
              description=""
              selectedItems={localPreferredBrands}
              onItemsChange={handlePreferredBrandsChange}
              placeholder="Add brand category..."
              suggestions={brandCategorySuggestions}
              helperText={preferenceInputHelp}
              saveStatus={saveStatuses.preferredBrands}
              showHelperOnFocusOnly
              reserveHelperSpace
            />
          </div>
        </div>

        <Button
          type="button"
          onClick={handleSaveAdditionalInfo}
          disabled={isSaving}
          variant="lime"
          className="w-full h-12 rounded-lg font-semibold"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </CardContent>
    </Card>
  )
}

export default AdditionalInfoSettings
